import { forwardRef, type ComponentPropsWithoutRef } from 'react'
import { cn } from '../../utils/cn'

type ButtonVariant = 'primary' | 'outline' | 'secondary' | 'danger'
type ButtonSize = 'md' | 'sm' | 'icon'

const baseClassName =
  'relative inline-flex items-center justify-center whitespace-nowrap font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-60'
const variantClassName: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-white shadow-accent hover:bg-accent-mid hover:-translate-y-0.5 hover:shadow-accent-strong disabled:shadow-none disabled:translate-y-0',
  outline:
    'border border-ink/15 bg-white/80 text-ink hover:-translate-y-0.5 hover:border-ink/30 hover:bg-white disabled:translate-y-0',
  secondary:
    'border border-ink/15 bg-surface/80 text-ink hover:-translate-y-0.5 hover:border-ink/30 hover:bg-white disabled:translate-y-0',
  danger:
    'bg-red-600 text-white hover:bg-red-700 hover:-translate-y-0.5 disabled:translate-y-0',
}
const sizeClassName: Record<ButtonSize, string> = {
  md: 'rounded-[var(--btn-radius)] px-5 py-3 text-sm',
  sm: 'rounded-[var(--btn-radius)] px-4 py-2 text-xs',
  // 正方形アイコン専用ボタンのため、文字ボタンの --btn-radius とは独立して rounded-2xl を維持
  icon: 'h-11 w-11 rounded-2xl p-0',
}

type ButtonProps = ComponentPropsWithoutRef<'button'> & {
  fullWidth?: boolean
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      fullWidth,
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        className={cn(
          baseClassName,
          variantClassName[variant],
          sizeClassName[size],
          fullWidth && 'w-full',
          className,
        )}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && (
          <span
            className="absolute inset-0 flex items-center justify-center"
            aria-hidden="true"
          >
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          </span>
        )}
        <span
          className={cn('inline-flex items-center', loading && 'opacity-0')}
        >
          {props.children}
        </span>
      </button>
    )
  },
)
